<template>
  <ToolShell
    tool-name="Payment reminder generator"
    h1="Payment reminder generator for overdue invoices."
    lede="A payment reminder is a short message that tells a client which invoice is unpaid, how much it is, when it was due and how to pay it now. Enter the invoice details and pick a tone, friendly, firm or final, to get one for email or SMS. Free, no signup."
    :trust="['No signup', 'Email & SMS versions', 'Ready to copy']"
    form-title="Reminder details"
    :upsell-points="[
      'Reminder emails send on a schedule; SMS starts on Voyager',
      'Sequences branch on whether the invoice is still unpaid',
      'A task is created for a person when it’s time to call',
    ]"
    free-label="The free generator"
    :free-bullets="[
      'One reminder message, ready to copy and send yourself',
      'You remember when each follow-up goes out',
      'Paid status lives in your bank app, not next to the invoice',
    ]"
    upgrade-headline="This message works once. Workspace369 makes it a workflow."
    :connected-bullets="[
      'Reminder emails send on a schedule on every plan, and SMS from Voyager',
      'Follow-ups branch on paid status automatically',
      'A task is created when it’s time to call',
      'Everything lands on the client record — status always visible',
    ]"
    slug="/tools/payment-reminder-generator"
    :meta-description="metaDescription"
    doc-brand="Payment reminder"
    :doc-subtitle="reminderSummary"
    :doc-kind="form.channel === 'email' ? 'EMAIL' : 'SMS'"
    stamp="Draft"
  >
    <template #form>
      <label class="ta-field">
        <span>Client name</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="5.2" r="2.7" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M2.8 14c.7-2.6 2.7-4 5.2-4s4.5 1.4 5.2 4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
          <input v-model.trim="form.client" type="text" placeholder="Maya" />
        </div>
      </label>

      <div class="ta-field-row">
        <label class="ta-field">
          <span>Invoice number</span>
          <div class="ta-input">
            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M5.5 2.5 4 13.5M12 2.5 10.5 13.5M3 5.5h10.5M2.5 10.5H13" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
            <input v-model.trim="form.number" type="text" placeholder="26-4" />
          </div>
        </label>
        <label class="ta-field">
          <span>Amount ($)</span>
          <div class="ta-input">
            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5v13M11.5 4.5c-.6-1.2-1.9-1.8-3.5-1.8-1.9 0-3.2.9-3.2 2.4 0 3.4 7 1.7 7 5.1 0 1.6-1.5 2.5-3.8 2.5-1.8 0-3.2-.8-3.8-2" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
            <input v-model.number="form.amount" type="number" min="0" step="0.01" placeholder="2400" />
          </div>
        </label>
      </div>

      <div class="ta-field">
        <span id="reminder-due-label">Due date</span>
        <ToolDropdown
          v-model="dueOffset"
          :options="dueOptions"
          aria-label="Due date"
          labelledby="reminder-due-label"
        />
      </div>

      <div class="ta-field-row">
        <div class="ta-field">
          <span id="reminder-tone-label">Tone</span>
          <ToolDropdown
            v-model="form.tone"
            :options="toneOptions"
            aria-label="Tone"
            labelledby="reminder-tone-label"
          />
        </div>
        <div class="ta-field">
          <span id="reminder-channel-label">Channel</span>
          <ToolDropdown
            v-model="form.channel"
            :options="channelOptions"
            aria-label="Channel"
            labelledby="reminder-channel-label"
          />
        </div>
      </div>

      <button type="button" class="ta-download" @click="copyText">
        {{ copied ? "Copied!" : "Copy reminder" }}
        <svg v-if="!copied" viewBox="0 0 16 16" aria-hidden="true"><rect x="5" y="5" width="9" height="9" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M11 5V3.5A1.5 1.5 0 0 0 9.5 2H3.5A1.5 1.5 0 0 0 2 3.5v6A1.5 1.5 0 0 0 3.5 11H5" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
        <svg v-else viewBox="0 0 16 16" aria-hidden="true"><path d="M13.3 4.3 6.4 11.2 2.7 7.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>

      <p class="ta-note">
        Chasing the fee too? Work it out first with the
        <NuxtLink :to="localePath('/tools/late-fee-calculator')">late fee calculator</NuxtLink>.
      </p>
    </template>

    <template #preview>
      <div class="ta-doc-meta">
        <div><span>To</span><strong>{{ form.client || "Client name" }}</strong></div>
        <div><span>Invoice #</span><strong>{{ inv }}</strong></div>
        <div><span>Amount</span><strong>{{ fmtAmount(form.amount) }}</strong></div>
        <div><span>Due</span><strong>{{ dueLabel }}</strong></div>
        <div v-if="form.channel === 'email'"><span>Subject</span><strong>{{ subject }}</strong></div>
      </div>

      <p class="ta-doc-body">{{ message }}</p>

      <p class="ta-doc-note">Include the same direct payment link in every reminder — every extra step costs days.</p>
    </template>

    <template #steps>
      <div><span>01</span><div><h2>Enter the invoice details</h2><p>Client, invoice number, amount, and when it was due.</p></div></div>
      <div><span>02</span><div><h2>Pick tone and channel</h2><p>Friendly nudge, firm follow-up, or final notice — as an email or an SMS.</p></div></div>
      <div><span>03</span><div><h2>Copy and send</h2><p>The message updates live; one click copies it, subject line included for email.</p></div></div>
    </template>

    <ToolGuide :sections="guideSections" faq-title="Payment reminder FAQ" :faq="faq" />

    <RelatedPages path="/tools/payment-reminder-generator" title="Payment reminder generator" />

    <Cta03 />
  </ToolShell>
</template>

<script setup>
import ToolDropdown from "~/components/tools/ToolDropdown.vue";
import ToolGuide from "~/components/tools/ToolGuide.vue";
import ToolShell from "~/components/tools/ToolShell.vue";

const { localePath } = useWorkspaceLocale();

const form = reactive({
  client: "",
  number: "",
  amount: null,
  tone: "friendly",
  channel: "email",
});

const copied = ref(false);

// Dates are client-only: prerendered HTML must stay identical day to day or
// the localization pipeline fails with "Rendered site copy changed".
const now = ref(null);
onMounted(() => {
  now.value = new Date();
});

const dueOffset = ref(null);

const dateForOffset = (offset) => {
  if (!now.value || offset === null) return "";
  const d = new Date(now.value);
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
};

const dueDate = computed(() => dateForOffset(dueOffset.value));

const dueOptions = computed(() =>
  [
    { value: null, label: "Not set" },
    { value: 0, label: "Today" },
    { value: -1, label: "Yesterday" },
    { value: -3, label: "3 days ago" },
    { value: -7, label: "7 days ago" },
    { value: -14, label: "14 days ago" },
    { value: -30, label: "30 days ago" },
  ].map((option) => ({
    ...option,
    hint: option.value === null ? "" : fmtDate(dateForOffset(option.value), ""),
  })),
);

const toneOptions = [
  { value: "friendly", label: "Friendly nudge" },
  { value: "firm", label: "Firm follow-up" },
  { value: "final", label: "Final notice" },
];

const channelOptions = [
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
];

const fmtDate = (v, fallback = "the due date") =>
  v ? new Date(`${v}T00:00:00`).toLocaleDateString("en-US", { month: "long", day: "numeric" }) : fallback;
const fmtAmount = (v) =>
  `$${(Number(v) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const client = computed(() => form.client || "there");
const inv = computed(() => form.number || "[invoice #]");

const dueLabel = computed(() => fmtDate(dueDate.value));

const reminderSummary = computed(() => {
  const tone = toneOptions.find((option) => option.value === form.tone)?.label || "";
  const channel = channelOptions.find((option) => option.value === form.channel)?.label || "";
  return `${tone} · ${channel}`;
});

const subject = computed(() => {
  if (form.tone === "friendly") return `Quick note on invoice ${inv.value}`;
  if (form.tone === "firm") return `Invoice ${inv.value} — now past due`;
  return `Final note on invoice ${inv.value}`;
});

const message = computed(() => {
  const amt = fmtAmount(form.amount);
  const due = fmtDate(dueDate.value);
  const link = "[payment link]";
  if (form.tone === "friendly") {
    return form.channel === "email"
      ? `Hi ${client.value} — a quick note that invoice ${inv.value} for ${amt} was due ${due}. Here is the link to review and pay: ${link}. Happy to answer anything.`
      : `Hi ${client.value} — friendly reminder that invoice ${inv.value} for ${amt} was due ${due}. Pay here: ${link}`;
  }
  if (form.tone === "firm") {
    return form.channel === "email"
      ? `Hi ${client.value} — invoice ${inv.value} for ${amt} was due ${due} and is still open. Could you let me know when to expect payment? The link is here if helpful: ${link}.`
      : `Hi ${client.value} — invoice ${inv.value} (${amt}) is past due since ${due}. Please pay here when you can: ${link}`;
  }
  return form.channel === "email"
    ? `Hi ${client.value} — invoice ${inv.value} for ${amt} is now well past its ${due} due date. Please arrange payment promptly or contact me to agree on a plan. Per our agreement, further work pauses until the balance is settled. ${link}`
    : `Hi ${client.value} — final notice: invoice ${inv.value} (${amt}) is overdue. Please pay here: ${link}, or contact me to make a plan.`;
});

const copyText = async () => {
  const text = form.channel === "email" ? `Subject: ${subject.value}\n\n${message.value}` : message.value;
  try {
    await navigator.clipboard.writeText(text);
    copied.value = true;
    setTimeout(() => (copied.value = false), 1600);
  } catch { /* clipboard unavailable */ }
};

const guideSections = [
  {
    title: "When to send each reminder",
    paragraphs: [
      "Each tone fits a point in the timeline. The due date options in the generator run from today to 30 days ago, so you can match the message to how late the invoice is.",
    ],
    bullets: [
      "Friendly nudge: on the due date or a day or two after. A late invoice is often just a missed one, so start friendly.",
      "Firm follow-up: inside the first week. It says the invoice is still open and asks when to expect payment.",
      "Final notice: around two weeks late. It asks for payment or a plan, and says what happens next.",
      "After the final notice, call: a conversation can settle what a fourth email may not.",
    ],
  },
  {
    title: "What each message says",
    paragraphs: [
      "Every version names the invoice number and the amount and includes the payment link, and all but the final-notice text also name the due date. The client should never have to look anything up to pay.",
      "The friendly email offers to answer questions. The firm follow-up asks the client to say when payment will arrive. The final notice asks them to pay or agree a plan; the email version also says further work pauses until the balance is settled.",
      "Only keep that last line if your contract or terms say work can pause for non-payment. If they do not, take it out before you send.",
    ],
  },
  {
    title: "Before you send it",
    bullets: [
      "Replace [payment link] with the link that opens the invoice and takes payment.",
      "Check the payment has not already arrived. A reminder for a paid invoice costs more goodwill than a late one.",
      "Send it to the person who pays the invoices, not only the person you work with.",
      "Reply in the same email thread as the invoice, so the history is in one place.",
    ],
  },
  {
    title: "Email or SMS",
    paragraphs: [
      "Email suits invoices with several people involved and anything you may need to point back to later; the email version adds a subject line. SMS suits small invoices and clients who already text you, and the SMS versions are shorter. Only text clients who have agreed to get texts from you.",
    ],
  },
  {
    title: "If the invoice still is not paid",
    paragraphs: [
      "Call and ask what is in the way. Offer a payment plan if the amount is the problem, and agree the dates in writing. If your terms include a late fee, work it out and tell the client before you add it. For anything beyond that, get advice from someone qualified where you work.",
    ],
    links: [
      { label: "Late fee calculator", path: "/tools/late-fee-calculator" },
      { label: "How to get paid faster", path: "/learn/how-to-get-paid-faster" },
    ],
  },
  {
    title: "Payment reminders in Workspace369",
    paragraphs: [
      "Late-payment reminders are on every plan. Workflow automations send them by email on every plan and by text from Voyager, wait between steps, stop when the invoice is paid, and can create a task when it is time to call. Payment plans start on Specialist, and automatic late fees on Commander.",
    ],
    links: [
      { label: "Workflow automations", path: "/features/automation" },
      { label: "Invoices", path: "/features/invoices" },
    ],
  },
];

const faq = [
  {
    q: "How many reminders should I send?",
    a: "A common rhythm is a friendly nudge before or just after the due date, a firm follow-up inside the first week, and a final notice around two weeks — each shorter and more direct than the last.",
  },
  {
    q: "Should I include the payment link every time?",
    a: "Yes. Every extra step between the reminder and paying costs days. Include the same direct payment link in every message, email or SMS.",
  },
  {
    q: "Is it rude to send a payment reminder on the due date?",
    a: "No. A short, friendly note on the due date is normal and expected. Keep it to the facts: the invoice, the amount and the link. Save firmer wording for later messages.",
  },
  {
    q: "What subject line should a payment reminder email have?",
    a: "Put the invoice number in it so the client can find the invoice. The generator uses “Quick note on invoice [number]” for the friendly nudge, “Invoice [number] — now past due” for the firm follow-up and “Final note on invoice [number]” for the final notice.",
  },
  {
    q: "Can reminders be automated?",
    a: "Yes. Workspace369 workflow automations send invoice follow-up emails on every plan, and SMS from Voyager, on a schedule, branch on whether the invoice is still unpaid, and create a task for a person when it is time to call.",
  },
];

const metaTitle = "Payment Reminder Generator | Workspace369 Free Tools";
const metaDescription =
  "Generate polite, firm, or final payment reminders for overdue invoices in seconds. Email and SMS versions, ready to copy — free, no signup.";

useHead(() => ({
  title: metaTitle,
  meta: [
    { name: "description", content: metaDescription },
    { name: "twitter:title", content: metaTitle },
    { name: "twitter:description", content: metaDescription },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: metaTitle },
    { property: "og:description", content: metaDescription },
  ],
}));
</script>
